import type { ComponentPropsWithoutRef } from 'react';
import { useState } from 'react';

import { Transition } from '@headlessui/react';

import { useFeatureFlag } from '@/utils/hooks';
import { ADD_MEDIA, REMOVE_MEDIA, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import { classNames } from '@/utils';
import { SM } from '@/types/utils/sizesType';

import { ALBUM_LIMIT } from '@/components/pages/albums/AlbumFunnelModal/constants/albumLimit';

import Checkbox from '@/components/base/Checkbox';
import { SocialProfileIndicator } from '@/components/base/Post/SocialProfileIndicator';
import AlbumPost from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/post/index';

type MediaType = {
    id: string;
    social_profile_id?: number;
};

type PostButtonProps = ComponentPropsWithoutRef<'div'> & {
    media: MediaType;
};

export function PostButton({ media, ...restProps }: PostButtonProps) {
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const [isHover, setIsHover] = useState(false);
    const isGroup = useIsGroup();
    const campaignFeatureFlagEnabled = useFeatureFlag('campaign_media_limit_500_active');

    // Check if media is selected - medias are always objects with { id, social_profile_id }
    const isChecked = albumInfo?.medias?.some((m: MediaType | string) => {
        const mediaId = typeof m === 'object' ? m.id : m;
        return mediaId === media?.id;
    });
    const isDisabled = (albumInfo?.medias?.length ?? 0) >= ALBUM_LIMIT && !isChecked;

    const getMediaNetwork = () => {
        if (!isGroup) {
            return activeSocialProfile?.social_network_type;
        }

        const mediaProfile = activeGroup?.social_profiles?.find(
            (profile) => profile.id === media.social_profile_id
        );

        return mediaProfile?.social_network_type || activeSocialProfile?.social_network_type;
    };

    const handleToggleMedia = () => {
        const atLimit = (albumInfo?.medias?.length ?? 0) >= ALBUM_LIMIT;
        if (!isChecked && atLimit && campaignFeatureFlagEnabled) {
            return;
        }

        const socialProfileId = media.social_profile_id || activeSocialProfile?.id;

        const payload: MediaType = {
            id: media.id,
            ...(socialProfileId && { social_profile_id: socialProfileId })
        };

        dispatch({
            type: isChecked ? REMOVE_MEDIA : ADD_MEDIA,
            payload
        });
    };

    return campaignFeatureFlagEnabled ? (
        <div className="z-0 dark:bg-icoDarkMode-anthracite" {...restProps}>
            {isGroup && (
                <SocialProfileIndicator
                    socialProfileId={media.social_profile_id || activeSocialProfile?.id}
                    className="mb-2"
                    size={SM}
                />
            )}

            <div className="relative">
                <button
                    data-testid={`album-manual-post-button-${media.id}`}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={handleToggleMedia}
                    className={classNames(
                        'height-[unset] absolute inset-0 z-20 transition-all duration-150',
                        {
                            'pointer-events-none cursor-not-allowed opacity-50': isDisabled,
                            'hover:bg-icoBlack/40 focus:ring-0 focus:ring-offset-0 focus:ring-offset-transparent':
                                !isDisabled
                        }
                    )}
                    key={media?.id}>
                    <Transition
                        show={isChecked || isHover || isDisabled}
                        enter="transition-opacity duration-150"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <span
                            className={classNames(
                                'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-m',
                                {
                                    'bg-icoBlack/10 backdrop-blur-20': !isChecked,
                                    'bg-icoBlue': isChecked
                                }
                            )}>
                            {!isDisabled && (
                                <Checkbox
                                    checked={isChecked}
                                    onChange={() => {
                                        //do nothing
                                    }}
                                    boxStyle="white"
                                    boxSize="xs"
                                    className="focus:ring-0"
                                />
                            )}
                        </span>
                    </Transition>
                </button>
                <AlbumPost
                    shouldDisplayIcon={!isChecked && !isHover && !isDisabled}
                    data={media}
                    network={getMediaNetwork()}
                    albumType={albumInfo?.criteria?.album_type}
                />
            </div>
        </div>
    ) : (
        <div className="z-0 dark:bg-icoDarkMode-anthracite" {...restProps}>
            {isGroup && (
                <SocialProfileIndicator
                    socialProfileId={media.social_profile_id || activeSocialProfile?.id}
                    className="mb-2"
                    size={SM}
                />
            )}

            <div className="relative">
                <button
                    data-testid={`album-manual-post-button-${media.id}`}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={handleToggleMedia}
                    className="height-[unset] absolute inset-0 z-20 transition-all duration-150 hover:bg-icoBlack/40 focus:ring-0 focus:ring-offset-0 focus:ring-offset-transparent"
                    key={media?.id}>
                    <Transition
                        show={isChecked || isHover}
                        enter="transition-opacity duration-150"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <span
                            className={classNames(
                                'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-m',
                                {
                                    'bg-icoBlack/10 backdrop-blur-20': !isChecked,
                                    'bg-icoBlue': isChecked
                                }
                            )}>
                            <Checkbox
                                checked={isChecked}
                                onChange={() => {
                                    //do nothing
                                }}
                                boxStyle="white"
                                boxSize="xs"
                                className="focus:ring-0"
                            />
                        </span>
                    </Transition>
                </button>
                <AlbumPost
                    shouldDisplayIcon={!isChecked && !isHover}
                    data={media}
                    network={getMediaNetwork()}
                    albumType={albumInfo?.criteria?.album_type}
                />
            </div>
        </div>
    );
}
